/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ID } from '@gh/common-shared-data';
import { getScrollbarHeight, getScrollbarWidth, onAnimationFrame$ } from '@gh/core-util';
import { fromEvent$ } from '@gh/rx/index';
import { clamp } from 'lodash';
import { PaperScope } from 'paper';
import { Subscription } from 'rxjs';
import {
  GraphicalTakeOffCommandService,
  ViewportManager,
} from '../../graphical-take-off-command.service';
import { GraphicalTakeOffFormState } from '../../graphical-take-off-form.service';
import { getGraphicalTakeOffTopViewId, GraphicalTakeOffTopView } from '../../resources/models';

@Component({
  standalone: false,
  selector: 'gh-graphical-take-off-canvas',
  templateUrl: 'graphical-take-off-canvas.component.html',
  styleUrls: ['graphical-take-off-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GraphicalTakeOffCanvasComponent implements OnInit, OnDestroy {
  @ViewChild('canvasElement', { static: true }) canvasElement: ElementRef;
  @ViewChild('scrollContainer', { static: true }) scrollContainerElement: ElementRef;
  @ViewChild('scrollPlaceholder', { static: true }) scrollPlaceholderElement: ElementRef;

  scope: PaperScope;

  private scrollSubscription: Subscription;

  constructor(private ngZone: NgZone,
              public form: GraphicalTakeOffFormState,
              private commands: GraphicalTakeOffCommandService) {

  }

  topTrackBy(_: number, top: GraphicalTakeOffTopView): ID {
    return getGraphicalTakeOffTopViewId(top);
  }

  ngOnInit(): void {
    this.canvasElement.nativeElement.style.width = `calc(100% - ${getScrollbarWidth()}px)`;
    this.canvasElement.nativeElement.style.height = `calc(100% - ${getScrollbarHeight()}px)`;

    this.scope = new PaperScope();
    this.scope.setup(this.canvasElement.nativeElement);
    this.scope.activate();

    this.commands.initViewportManager(this.createViewportManager());

    this.ngZone.runOutsideAngular(() => {
      this.scrollSubscription = fromEvent$(this.scrollContainerElement.nativeElement, 'scroll')
        .pipe(onAnimationFrame$)
        .subscribe(() => {
          this.ngZone.run(() => {
            this.onScroll();
          });
        });
    });
  }

  ngOnDestroy(): void {
    this.scope.project.remove();
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  onFocusViewport(): void {
    this.scrollContainerElement.nativeElement.focus();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.commands.updateWindowSize();
  }

  onScroll(): void {
    const { scrollLeft, scrollTop } = this.scrollContainerElement.nativeElement;
    this.commands.setViewportPosition(scrollLeft, scrollTop);
  }

  onWheel(event: WheelEvent): void {
    if (event.ctrlKey) {
      this.wheelZoom(event);
    } else {
      this.wheelScroll(event);
    }
  }

  private wheelZoom(event: WheelEvent): void {
    event.preventDefault();

    const delta = event.deltaY || event.deltaX || 0;
    this.commands.zoomAroundViewPoint(event.offsetX, event.offsetY, Math.sign(delta));
  }

  private wheelScroll(event: WheelEvent): void {
    const scrollContainerElement = this.scrollContainerElement.nativeElement;
    const deltaX = event.deltaX || event.shiftKey && event.deltaY || 0;
    const deltaY = !event.shiftKey && event.deltaY || 0;

    if (deltaX && scrollContainerElement.scrollWidth > scrollContainerElement.clientWidth) {
      scrollContainerElement.scrollLeft = clamp(
        scrollContainerElement.scrollLeft + 120 * Math.sign(deltaX),
        0, scrollContainerElement.scrollWidth - scrollContainerElement.clientWidth);
    }
    if (deltaY && scrollContainerElement.scrollHeight > scrollContainerElement.clientHeight) {
      scrollContainerElement.scrollTop = clamp(
        scrollContainerElement.scrollTop + 120 * Math.sign(deltaY),
        0, scrollContainerElement.scrollHeight - scrollContainerElement.clientHeight);
    }
  }

  private createViewportManager(): ViewportManager {
    return {
      getScrollLeft: () => this.scrollContainerElement.nativeElement.scrollLeft,
      getScrollTop: () => this.scrollContainerElement.nativeElement.scrollTop,
      setScrollLeft: (left: number) => {
        this.scrollContainerElement.nativeElement.scrollLeft = left;
      },
      setScrollTop: (top: number) => {
        this.scrollContainerElement.nativeElement.scrollTop = top;
      },
      setScrollWidth: (width: number) => {
        this.scrollPlaceholderElement.nativeElement.style.width = `${width}px`;
      },
      setScrollHeight: (height: number) => {
        this.scrollPlaceholderElement.nativeElement.style.height = `${height}px`;
      },
    };
  }
}
